import { useMutation, useQuery } from '@tanstack/react-query';
import { contentApi, type ContentItemAction } from '@/api/content';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import type { ContentItemView, ContentProfileInput, SiteContentView } from '@/types/api';

/** Asked again while the worker has something of this site in hand. */
const busy = (view: SiteContentView | undefined) =>
  view !== undefined && (view.ideasRequested > 0 || view.items.some((item) => item.state === 'queued' || item.state === 'writing' || item.state === 'publishing'));

export function useContentOverview() {
  return useQuery({ queryKey: queryKeys.content.overview, queryFn: contentApi.overview, refetchInterval: 60_000 });
}

export function useSiteContent(siteId: string) {
  return useQuery({
    queryKey: queryKeys.content.site(siteId),
    queryFn: () => contentApi.site(siteId),
    refetchInterval: (query) => (busy(query.state.data) ? 5_000 : false),
  });
}

/** One article, asked again every three seconds while it is queued, written or sent to the CMS. */
export function useContentItem(id: string | null) {
  return useQuery({
    queryKey: queryKeys.content.item(id ?? ''),
    queryFn: () => contentApi.item(id!),
    enabled: id !== null,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === 'queued' || state === 'writing' || state === 'publishing' ? 3_000 : false;
    },
  });
}

const everything = () => [queryKeys.content.all];

export function useSaveContentProfile(siteId: string, callbacks?: MutationCallbacks<SiteContentView, ContentProfileInput>) {
  return useApiMutation((input: ContentProfileInput) => contentApi.saveProfile(siteId, input), everything, callbacks);
}

export function useCheckContentAccess(siteId: string) {
  return useMutation({ mutationFn: () => contentApi.check(siteId) });
}

export function useRequestIdeas(siteId: string, callbacks?: MutationCallbacks<{ requested: boolean }, number>) {
  return useApiMutation((count: number) => contentApi.requestIdeas(siteId, count), everything, callbacks);
}

export function useAddIdea(siteId: string, callbacks?: MutationCallbacks<ContentItemView, { title: string; keyword: string | null; brief: string | null; write: boolean }>) {
  return useApiMutation((input: { title: string; keyword: string | null; brief: string | null; write: boolean }) => contentApi.addIdea(siteId, input), everything, callbacks);
}

export function useEditContent(id: string, callbacks?: MutationCallbacks<ContentItemView, Record<string, unknown>>) {
  return useApiMutation((input: Record<string, unknown>) => contentApi.edit(id, input), everything, callbacks);
}

export function useContentAction(callbacks?: MutationCallbacks<ContentItemView, { id: string; action: ContentItemAction; body?: Record<string, unknown> }>) {
  return useApiMutation(({ id, action, body }: { id: string; action: ContentItemAction; body?: Record<string, unknown> }) => contentApi.act(id, action, body), everything, callbacks);
}
